/**
 * API Endpoints Configuration
 * 
 * Centralized configuration for all API endpoints used in the application.
 * This includes both existing endpoints and new master/sub-account endpoints.
 */

// ============================================================================
// BASE CONFIGURATION
// ============================================================================

// Get server region from environment (supports both Vite and Next.js)
const getServerRegion = () => {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return import.meta.env.VITE_SERVER_REGION
  }
  if (typeof process !== 'undefined' && process.env) {
    return process.env.NEXT_PUBLIC_SERVER_REGION
  }
  return 'GLOBAL'
}

const SERVER_REGION = getServerRegion()

export const API_CONFIG = {
  BASE_URL: SERVER_REGION === 'CN' 
    ? 'https://api.aivisionfood.cn' 
    : 'https://api.aivisionfood.com',
  TIMEOUT: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second
} as const;

// ============================================================================
// REGION-AWARE ENDPOINT HELPERS
// ============================================================================

/**
 * Get region-specific endpoint
 * China uses /api/authCN/* for authentication, Global uses /api/users/*
 * @param globalPath - Endpoint path for Global region
 * @param chinaPath - Endpoint path for China region
 * @returns Region-appropriate endpoint path
 */
const getRegionEndpoint = (globalPath: string, chinaPath: string): string => {
  return SERVER_REGION === 'CN' ? chinaPath : globalPath;
};

// ============================================================================
// AUTHENTICATION ENDPOINTS
// ============================================================================

export const AUTH_ENDPOINTS = {
  // Regular authentication - Region specific
  LOGIN: getRegionEndpoint('/api/users/login', '/api/authCN/login'),
  REGISTER: getRegionEndpoint('/api/users/', '/api/authCN'),
  CHANGE_PASSWORD: getRegionEndpoint('/api/users/password', '/api/authCN/changePassword'),
  
  // These remain the same across regions
  VERIFY_ACCOUNT: '/api/users/verify',
  RESEND_VERIFICATION: '/api/users/resend',
  FORGOT_PASSWORD: '/api/users/password/forgot',
  RESET_PASSWORD: '/api/users/password/forgot',
  VERIFY_TOKEN: '/api/users/trap',
  
  // Enhanced user info
  GET_USER_INFO: '/api/users/',
  UPDATE_USER: '/api/users/update',
  CHANGE_FILTER_OPTIONS: '/api/users/changeFilterOptions',
  TOGGLE_FAHRENHEIT: '/api/users/fahrenheit',
  CHANGE_INDICATORS: '/api/users/changeIndicators',
  
  // Master/Sub-Account authentication (same for both regions)
  SUB_ACCOUNT_LOGIN: '/api/master/subLogin',
} as const;

// ============================================================================
// MASTER ACCOUNT MANAGEMENT ENDPOINTS
// ============================================================================

export const MASTER_ACCOUNT_ENDPOINTS = {
  // Sub-account management
  CREATE_SUB_ACCOUNT: '/api/master/createSubAccount',
  GET_MY_SUB_ACCOUNTS: '/api/master/mySubAccounts',
  TOGGLE_SUB_STATUS: '/api/master/toggleSubStatus',
  CHANGE_SUB_PASSWORD: '/api/master/changeSubAccountPassword',
  DELETE_SUB_ACCOUNT: '/api/master/deleteSubAccount',
  
  // SmartProbe management
  MOVE_SMARTPROBE: '/api/master/moveSmartProbe',
} as const;

// ============================================================================
// TEAM MANAGEMENT ENDPOINTS
// ============================================================================

export const TEAM_ENDPOINTS = {
  GET_TEAM: '/api/teams/getTeam',
  CREATE_TEAM: '/api/teams/create',
  UPDATE_TEAM: '/api/teams/update',
  DELETE_TEAM: '/api/teams/delete',
} as const;

// ============================================================================
// SMARTPROBE DATA ENDPOINTS
// ============================================================================

export const SMARTPROBE_ENDPOINTS = {
  // Probe information
  GET_TRAP_INFO: '/api/traps/{id}',
  UPDATE_TRAP: '/api/traps/update/{id}',
  
  // Probe data
  GET_TRAP_DATA: '/api/data/trapProbe/{id}',
  GET_SCROLL_INFO: '/api/data/scrollinfo/probes/{num}',
  GET_SCROLL_INFO_V2: '/api/data/scrollinfo/probesV2',
  FETCH_SCROLL_INFO: '/api/data/scrollinfo/probes',
  
  // Insect management
  CHANGE_INSECT_COUNT: '/api/insect/changeInsectCount',
  
  // Data management
  DELETE_TRAP_PUSH: '/api/data/trapTeams',
  REMOVE_TRAP: '/api/data/trapTeams/session/remove',
  CLEAN_TRAP: '/api/probefunction/trapTeams/probeSession/all',
  DELETE_CURRENT_DATA: '/api/probefunction/trap/probeCurrent/delete',
  DELETE_ALL_DATA: '/api/probefunction/trapTeams/probeAll/delete',
} as const;

// ============================================================================
// NOTIFICATION ENDPOINTS
// ============================================================================

export const NOTIFICATION_ENDPOINTS = {
  TOGGLE_EMAIL_NOTIFICATION: '/api/notification',
  TOGGLE_EMAIL_NOTIFICATION_DAY: '/api/notification/days',
  ADD_EMAIL: '/api/notification/addEmail',
  REMOVE_EMAIL: '/api/notification/removeEmail',
} as const;

// ============================================================================
// DATA DOWNLOAD ENDPOINTS
// ============================================================================

export const DATA_DOWNLOAD_ENDPOINTS = {
  // Synchronous downloads
  EXPORT_DATA_TEAMS: '/api/dataDownload/exportdataTeams',
  DOWNLOAD_MONTHLY_REPORT: '/api/dataDownload/downloadMonthlyReport',
  
  // Asynchronous downloads - SmartProbe
  START_ASYNC_MONTHLY_REPORT: '/api/asyncDataDownload/startMonthlyReport',
  START_TEAMS_DATA_EXPORT: '/api/asyncDataDownload/startTeamsDataExport',
  CHECK_JOB_STATUS: '/api/asyncDataDownload/jobStatus/{jobId}',
  DOWNLOAD_ASYNC_REPORT: '/api/asyncDataDownload/download/{jobId}',
  
  // Asynchronous downloads - Rodent Sensors
  START_RODENT_TEAMS_DATA_EXPORT: '/api/asyncDataDownload/startRodentTeamsDataExport',
  START_RODENT_MONTHLY_REPORT: '/api/asyncDataDownload/startRodentMonthlyReport',
} as const;

// ============================================================================
// RODENT PROBE ENDPOINTS
// ============================================================================

export const RODENT_PROBE_ENDPOINTS = {
  // Team management
  GET_TEAM_PROBES: '/api/rodentProbe/team',                     // POST { teamsName }
  // Probe CRUD
  UPDATE_RODENT_PROBE: '/api/rodentProbe/update',               // PUT { probeId, updates }
  REMOVE_RODENT_PROBE: '/api/rodentProbe',                      // DELETE /api/rodentProbe/:probeId
  // History
  GET_RODENT_PROBE_HISTORY: '/api/rodentProbe/history',         // GET /api/rodentProbe/history/:probeId
  DELETE_RODENT_PROBE_HISTORY: '/api/rodentProbe/history',      // DELETE /api/rodentProbe/history/:probeId
  DELETE_RODENT_PROBE_HISTORY_ENTRY: '/api/rodentProbe/history', // DELETE /api/rodentProbe/history/:probeId/:entryId
} as const;

// ============================================================================
// RODENT ANALYTICS ENDPOINTS
// ============================================================================

export const RODENT_ANALYTICS_ENDPOINTS = {
  TRIGGERS_BY_DATE: '/api/rodentProbeAnalytics/triggersByDate', // POST { teamsName, year, month } - JWT
} as const;

// ============================================================================
// SESSION MANAGEMENT ENDPOINTS
// ============================================================================

export const SESSION_ENDPOINTS = {
  ALL_PROBE_DATA_FROM_ACCOUNT: '/api/session/allProbeDataFromAccount',
  GET_INSECTS_BY_SESSION_AND_TRAP: '/api/session/getInsectsBySessionAndTrap',
  MOVE_INSECTS_TO_DELETED: '/api/session/moveInsectsToDeleted',
} as const;

// ============================================================================
// ENDPOINT UTILITIES
// ============================================================================

/**
 * Replace path parameters in endpoint URLs
 * @param endpoint Endpoint with path parameters (e.g., '/api/traps/{id}')
 * @param params Object with parameter values (e.g., { id: '123' })
 * @returns Endpoint with parameters replaced
 */
export const buildEndpoint = (endpoint: string, params: Record<string, string | number> = {}): string => {
  let builtEndpoint = endpoint;
  
  Object.entries(params).forEach(([key, value]) => {
    builtEndpoint = builtEndpoint.replace(`{${key}}`, String(value));
  });
  
  return builtEndpoint;
};

/**
 * Build query string from parameters
 * @param params Query parameters object
 * @returns Query string (e.g., '?param1=value1&param2=value2')
 */
export const buildQueryString = (params: Record<string, any> = {}): string => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

/**
 * Get full URL for an endpoint
 * @param endpoint Relative endpoint path
 * @param params Path parameters to replace
 * @param queryParams Query parameters to append
 * @returns Full URL
 */
export const getFullUrl = (
  endpoint: string, 
  params: Record<string, string | number> = {},
  queryParams: Record<string, any> = {}
): string => {
  const builtEndpoint = buildEndpoint(endpoint, params);
  const queryString = buildQueryString(queryParams);
  return `${API_CONFIG.BASE_URL}${builtEndpoint}${queryString}`;
};

// ============================================================================
// ENDPOINT GROUPS FOR EASY ACCESS
// ============================================================================

export const API_ENDPOINTS = {
  AUTH: AUTH_ENDPOINTS,
  MASTER_ACCOUNT: MASTER_ACCOUNT_ENDPOINTS,
  TEAMS: TEAM_ENDPOINTS,
  SMARTPROBE: SMARTPROBE_ENDPOINTS,
  NOTIFICATIONS: NOTIFICATION_ENDPOINTS,
  DATA_DOWNLOAD: DATA_DOWNLOAD_ENDPOINTS,
  SESSIONS: SESSION_ENDPOINTS,
} as const;

// ============================================================================
// PERMISSION-BASED ENDPOINT ACCESS
// ============================================================================

/**
 * Get endpoints accessible by account type
 * @param accountType User's account type
 * @returns Object with accessible endpoints
 */
export const getAccessibleEndpoints = (accountType: 'master' | 'sub' | 'regular') => {
  const baseEndpoints = {
    auth: AUTH_ENDPOINTS,
    smartprobe: {
      ...SMARTPROBE_ENDPOINTS,
      // Sub-accounts cannot modify data
      ...(accountType === 'sub' ? {
        CHANGE_INSECT_COUNT: undefined,
        DELETE_TRAP_PUSH: undefined,
        REMOVE_TRAP: undefined,
        CLEAN_TRAP: undefined,
        DELETE_CURRENT_DATA: undefined,
        DELETE_ALL_DATA: undefined,
        UPDATE_TRAP: undefined,
      } : {}),
    },
    notifications: NOTIFICATION_ENDPOINTS,
    sessions: SESSION_ENDPOINTS,
  };

  // Master account gets all endpoints
  if (accountType === 'master') {
    return {
      ...baseEndpoints,
      masterAccount: MASTER_ACCOUNT_ENDPOINTS,
      teams: TEAM_ENDPOINTS,
      dataDownload: DATA_DOWNLOAD_ENDPOINTS,
    };
  }

  // Sub-accounts get limited access
  if (accountType === 'sub') {
    return {
      ...baseEndpoints,
      // Limited data download access
      dataDownload: {
        EXPORT_DATA_TEAMS: DATA_DOWNLOAD_ENDPOINTS.EXPORT_DATA_TEAMS,
        DOWNLOAD_MONTHLY_REPORT: DATA_DOWNLOAD_ENDPOINTS.DOWNLOAD_MONTHLY_REPORT,
      },
    };
  }

  // Regular accounts get standard access
  return {
    ...baseEndpoints,
    dataDownload: DATA_DOWNLOAD_ENDPOINTS,
  };
};

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Validate if endpoint exists
 * @param endpoint Endpoint to validate
 * @returns Whether endpoint exists in configuration
 */
export const isValidEndpoint = (endpoint: string): boolean => {
  const allEndpoints = Object.values(API_ENDPOINTS).flatMap(group => Object.values(group));
  return allEndpoints.includes(endpoint as any);
};

/**
 * Get endpoint category
 * @param endpoint Endpoint to categorize
 * @returns Category name or null if not found
 */
export const getEndpointCategory = (endpoint: string): string | null => {
  for (const [category, endpoints] of Object.entries(API_ENDPOINTS)) {
    if (Object.values(endpoints).includes(endpoint as any)) {
      return category.toLowerCase();
    }
  }
  return null;
};

// ============================================================================
// EXPORT ALL
// ============================================================================

export default {
  CONFIG: API_CONFIG,
  ENDPOINTS: API_ENDPOINTS,
  AUTH: AUTH_ENDPOINTS,
  MASTER_ACCOUNT: MASTER_ACCOUNT_ENDPOINTS,
  TEAMS: TEAM_ENDPOINTS,
  SMARTPROBE: SMARTPROBE_ENDPOINTS,
  NOTIFICATIONS: NOTIFICATION_ENDPOINTS,
  DATA_DOWNLOAD: DATA_DOWNLOAD_ENDPOINTS,
  SESSIONS: SESSION_ENDPOINTS,
  
  // Utilities
  buildEndpoint,
  buildQueryString,
  getFullUrl,
  getAccessibleEndpoints,
  isValidEndpoint,
  getEndpointCategory,
};









